import { AssignedAccessCode, CodeType, Transaction, TransactionApi } from 'apis/TransactionApi';
import { getMostRestrictedLicenseDeliverable } from 'utils/objects';
import { type IntlShape } from 'react-intl';

type ToastFn = (message: string, variant: 'success' | 'danger') => void;

// Duplicates an access code transaction (and seeds its initial access code), returning the new
// transaction id on success or null on failure. Shared by the Access Codes list and detail views
// so both can offer "Duplicate" without re-implementing the flow; the caller decides where to
// navigate afterwards.
export const duplicateTransaction = async (
    dataItem: Transaction,
    intl: IntlShape,
    createToast: ToastFn
): Promise<number | null> => {

    try {

        const partnerId = dataItem.PartnerId ?? null;

        const licenseData = await TransactionApi.GetAllLicenses(partnerId).catch(() => null);

        const duplicateProcess = await TransactionApi.Duplicate(dataItem.Id);

        if (!duplicateProcess.Done || !duplicateProcess.Id) {

            createToast(intl.formatMessage({ id: 'failed_to_duplicate_access_code_name.1465afbc', defaultMessage: 'Failed to duplicate access code: "{name}"' }, { name: dataItem.Name }), 'danger');
            return null;

        }

        const newTransaction = await TransactionApi.Get(duplicateProcess.Id, partnerId).catch(() => undefined);

        if (!newTransaction) {

            createToast(
                intl.formatMessage({ id: 'unable_to_load_duplicated_access_code_pl.e684c74e', defaultMessage: 'Unable to load duplicated access code. Please try again.' }),
                'danger'
            );
            return null;

        }

        const codesTiedToTransaction = await TransactionApi.GetAccessCodesByTransactionId(dataItem.Id, partnerId).catch((): AssignedAccessCode[] => []);
        const assignedContent = await TransactionApi.GetLimitedContentByTransactionId(dataItem.Id, partnerId).catch(() => []);

        if (!licenseData) {

            createToast(intl.formatMessage({ id: 'unable_to_load_license_information_for_t.e6809f34', defaultMessage: 'Unable to load license information for the duplicated access code' }), 'danger');
            return null;

        }

        const restrictedLicense = getMostRestrictedLicenseDeliverable(licenseData.licenses ?? [], assignedContent ?? []);

        if (!restrictedLicense.license) {

            createToast(intl.formatMessage({ id: 'unable_to_load_license_information_for_t.e6809f34', defaultMessage: 'Unable to load license information for the duplicated access code' }), 'danger');
            return null;

        }

        let maxUses = 0;
        let codeType = CodeType.Multiple;

        if (codesTiedToTransaction && codesTiedToTransaction.length > 0) {

            maxUses = codesTiedToTransaction[0].Redeemed + (codesTiedToTransaction[0].Remaining ?? 0);

        } else {

            codeType = CodeType.Single;
            maxUses = codesTiedToTransaction.reduce((sum, curr) => {

                if (curr.CodeType === CodeType.Single) {

                    return sum + curr.Redeemed + (curr.Remaining ?? 0);

                }
                return sum;

            }, 0);

            if (maxUses === 0) {

                maxUses = restrictedLicense.avaliableStudentsAllowed;

            }

        }

        const timeDifference = new Date(restrictedLicense.license.OrderExpireDate).getTime() - new Date().getTime();
        const dayDifference = timeDifference / (1000 * 3600 * 24);

        await TransactionApi.CreateAccessCodeForTransaction(
            newTransaction.Id,
            newTransaction.Identifier,
            codeType,
            Math.round(dayDifference),
            restrictedLicense.license.OrderExpireDate,
            maxUses,
            codeType === CodeType.Single ? true : false
        );

        createToast(intl.formatMessage({ id: 'access_code_was_duplicated_successfully.78de79f3', defaultMessage: 'Access code was duplicated successfully' }), 'success');
        return newTransaction.Id;

    } catch (e) {

        console.error(e);

        createToast(
            intl.formatMessage({ id: 'an_error_occurred_while_duplicating_the.fc8596ef', defaultMessage: 'An error occurred while duplicating the access code: "{name}"' }, { name: dataItem.Name }),
            'danger'
        );
        return null;

    }

};
